'use strict'
import { z } from 'zod/mini'

// ========== Schemas ==========

/** 組織情報スキーマ */
export const DepartmentSchema = z.object({
  department_code: z.string(),
  department_name: z.string(),
  department_short_name: z.optional(z.string()),
  segment_name: z.optional(z.string()),
  segment_code: z.optional(z.string()),
  status: z.optional(z.string())
})

/** 期間スキーマ */
export const PeriodSchema = z.object({
  key: z.string(),
  startDate: z.string(),
  endDate: z.optional(z.string()),
  label: z.string(),
  DepartmentHierarchyCount: z.number(),
  registration_status: z.string()
})

/** セグメントスキーマ */
export const SegmentSchema = z.object({
  segment_code: z.optional(z.string()),
  segment_name: z.optional(z.string())
})

/** 組織階層行スキーマ（7列：第1〜第6階層＋組織コード） */
export const HierarchyRowSchema = z.array(z.string())

// ========== Type Definitions ==========

/** @typedef {import('zod/mini').infer<typeof DepartmentSchema>} Department */
/** @typedef {import('zod/mini').infer<typeof PeriodSchema>} Period */
/** @typedef {import('zod/mini').infer<typeof SegmentSchema>} Segment */
/** @typedef {import('zod/mini').infer<typeof HierarchyRowSchema>} HierarchyRow */

// ========== API Response Schemas ==========

/** 組織情報取得レスポンス（IF02074） */
export const GetDepartmentInfoResponseSchema = z.object({
  body: z.optional(
    z.object({
      target_period: z.object({
        department_info: z.array(DepartmentSchema),
        segment_list: z.array(SegmentSchema)
      })
    })
  ),
  target_period: z.optional(
    z.object({
      department_info: z.array(DepartmentSchema),
      segment_list: z.array(SegmentSchema)
    })
  )
})

/** 組織階層取得レスポンス（IF02075） */
export const GetDepartmentHierarchyResponseSchema = z.object({
  body: z.optional(
    z.object({
      target_period: z.object({
        master: z.optional(z.any()),
        processed: z.optional(z.array(z.array(z.string())))
      })
    })
  ),
  target_period: z.optional(
    z.object({
      master: z.optional(z.any()),
      processed: z.optional(z.array(z.array(z.string())))
    })
  )
})

/** @typedef {import('zod/mini').infer<typeof GetDepartmentInfoResponseSchema>} GetDepartmentInfoResponse */
/** @typedef {import('zod/mini').infer<typeof GetDepartmentHierarchyResponseSchema>} GetDepartmentHierarchyResponse */

// ========== State Schema ==========

/**
 * 画面ストアの「主要フィールド」用 Zod スキーマ（推論・ドキュメント向け）。 実行時の reducer 状態（store.js の initialState）とキーが完全一致しない場合がある。真実の形状は store の JSDoc
 * DepartmentState および initialState を参照すること。
 */
export const DepartmentStateSchema = z.object({
  periodList: z.array(PeriodSchema),
  currentPeriodKey: z.string(),
  currentDepartments: z.array(DepartmentSchema),
  currentHierarchy: z.array(HierarchyRowSchema),
  hierarchyCount: z.number(),
  hierarchyDirty: z.boolean(),
  activeTab: z.string(),
  orgSortKey: z.string(),
  orgSortDir: z.enum(['asc', 'desc']),
  loading: z.boolean(),
  orgErrorCount: z.number(),
  hierarchyErrorCount: z.number(),
  exportDropdown: z.nullable(z.string()),
  segmentList: z.array(SegmentSchema),
  periodDataByKey: z.record(z.any()),
  initDone: z.boolean(),
  pendingPeriodKey: z.string(),
  topError: z.string()
})

/** @typedef {import('zod/mini').infer<typeof DepartmentStateSchema>} DepartmentState */

// ========== Component Props Schemas ==========

/** 組織情報登録・編集モーダルProps */
export const DepartmentOrgFormModalPropsSchema = z.object({
  isEdit: z.boolean(),
  initial: z.optional(
    z.object({
      department_code: z.optional(z.string()),
      department_name: z.optional(z.string()),
      department_short_name: z.optional(z.string()),
      segment_name: z.optional(z.string())
    })
  ),
  segmentList: z.array(z.union([z.string(), SegmentSchema])),
  existingCodes: z.optional(z.any()), // Set型はzod/miniで直接表現できないためanyを使用
  onClose: z.optional(z.any()), // 関数型はzod/miniで直接表現できないためanyを使用
  onSubmit: z.any(), // 関数型はzod/miniで直接表現できないためanyを使用（必須）
  onError: z.optional(z.any()) // 関数型はzod/miniで直接表現できないためanyを使用
})

/** 新規期間作成モーダルProps */
export const DepartmentNewPeriodModalPropsSchema = z.object({
  minStartDate: z.string(),
  existingPeriods: z.array(z.any()),
  onClose: z.optional(z.function()),
  onCreate: z.function(),
  onError: z.optional(z.function())
})

/** @typedef {import('zod/mini').infer<typeof DepartmentOrgFormModalPropsSchema>} DepartmentOrgFormModalProps */
/** @typedef {import('zod/mini').infer<typeof DepartmentNewPeriodModalPropsSchema>} DepartmentNewPeriodModalProps */
